function setLocalStorageItem( key, value ) {
    localStorage.setItem( key, value );
}

function getLocalStorageItem( key ) {
    // .getItem will return null if the key doesn't exist
    return localStorage.getItem( key ) || '';
}

function removeLocalStorageItem( key ) {
    localStorage.removeItem( key );
}

const USERNAME_KEY = 'logistics_username';
const PASSWORD_KEY = 'logistics_password';
const LOCATION_KEY = 'logistics_location';

export default {
    /* ---------- JWT Token ---------- */

    /**
     * @typedef { Object } jwtTokenInfo
     * @property { String } jwtTokenInfo.aud - "tolPortal"
     * @property { Number } jwtTokenInfo.exp - an expiration timestamp
     * @property { Number } jwtTokenInfo.iat - an "issued at" timestamp
     * @property { String } jwtTokenInfo.iss - the user's email address
     * @property { Number } jwtTokenInfo.locationId - the authorized location's id
     * @property { String } jwtTokenInfo.sub - the user's email address
     */
    /**
     * Parse a given jwt token and return the encoded object
     * Source: https://stackoverflow.com/a/38552302
     * @param { String } token - a JWT Token
     * @returns { jwtTokenInfo }
     */
    parseJwt( token ) {
        const base64Url = token.split( '.' )[ 1 ];
        const base64 = base64Url
            .replace( /-/g, '+' )
            .replace( /_/g, '/' );
        const jsonPayload = decodeURIComponent(
            atob( base64 )
                .split( '' )
                .map( ( c ) => `%${ ( `00${ c.charCodeAt( 0 ).toString( 16 ) }` ).slice( -2 ) }` )
                .join( '' ),
        );
        return JSON.parse( jsonPayload );
    },

    /* ---------- Username ---------- */

    /** @param { String } username */
    setLocalUsername( username ) {
        setLocalStorageItem( USERNAME_KEY, username );
    },

    /** @returns { String } */
    getLocalUsername() {
        return getLocalStorageItem( USERNAME_KEY );
    },

    /** Removes username from localStorage */
    removeLocalUsername() {
        removeLocalStorageItem( USERNAME_KEY );
    },

    /** @returns { Boolean } */
    hasLocalUsername() {
        return this.getLocalUsername() !== '';
    },

    /* ---------- Password ---------- */

    /** @param { String } password */
    setLocalPassword( password ) {
        setLocalStorageItem( PASSWORD_KEY, password );
    },

    /** @returns { String } */
    getLocalPassword() {
        return getLocalStorageItem( PASSWORD_KEY );
    },

    /** Removes password from localStorage */
    removeLocalPassword() {
        removeLocalStorageItem( PASSWORD_KEY );
    },

    /** @returns { Boolean } */
    hasLocalPassword() {
        return this.getLocalPassword() !== '';
    },

    /* ---------- Location ---------- */

    /** @param { Object } location */
    setLocalLocation( location ) {
        setLocalStorageItem( LOCATION_KEY, JSON.stringify( location ) );
    },

    /** @returns { Object | Null } */
    getLocalLocation() {
        // eslint-disable-next-line no-extra-boolean-cast
        return !!localStorage.getItem( LOCATION_KEY ) && ( JSON.parse( localStorage.getItem( LOCATION_KEY ) ).id !== null )
            ? JSON.parse( getLocalStorageItem( LOCATION_KEY ) )
            : null;
    },

    /** Removes location from localStorage */
    removeLocalLocation() {
        removeLocalStorageItem( LOCATION_KEY );
    },

    /** @returns { Boolean } */
    hasLocalLocation() {
        return this.getLocalLocation() !== null;
    },

    /* ---------- misc ---------- */

    /** @returns { Boolean } */
    hasLocalLoginCredentials() {
        return this.hasLocalUsername()
            && this.hasLocalPassword()
            && this.hasLocalLocation();
    },

    /** Remove ALL credentials from localStorage */
    removeAllLocalCredentials() {
        this.removeLocalUsername();
        this.removeLocalPassword();
        this.removeLocalLocation();
    },
};
